/**
 * Collection UI Utilities
 * 
 * Toast notifications and UI helpers
 * 
 * @package IPHub
 */

let toastTimeout = null;

/**
 * Show toast notification
 */
export function showToast(type, message, duration = 3000) {
    const toast = document.querySelector('[data-collection-toast]');
    if (!toast) return;

    const toastMessage = toast.querySelector('[data-toast-message]');
    const toastClose = toast.querySelector('[data-toast-close]');
    const successIcon = toast.querySelector('[data-icon-success]');
    const errorIcon = toast.querySelector('[data-icon-error]');

    /* Set message */
    if (toastMessage) {
        toastMessage.textContent = message;
    }

    /* Show appropriate icon */
    if (successIcon && errorIcon) {
        if (type === 'success') {
            successIcon.classList.remove('hidden');
            errorIcon.classList.add('hidden');
        } else {
            successIcon.classList.add('hidden');
            errorIcon.classList.remove('hidden');
        }
    }

    /* Show toast */
    toast.classList.remove('translate-x-[120%]');
    toast.classList.add('translate-x-0');

    /* Clear existing timeout */
    if (toastTimeout) {
        clearTimeout(toastTimeout);
    }

    /* Auto-hide toast */
    toastTimeout = setTimeout(() => {
        hideToast();
    }, duration);

    /* Close button */
    if (toastClose) {
        toastClose.onclick = () => {
            hideToast();
        };
    }
}

/**
 * Hide toast notification
 */
export function hideToast() {
    const toast = document.querySelector('[data-collection-toast]');
    if (!toast) return;

    toast.classList.remove('translate-x-0');
    toast.classList.add('translate-x-[120%]');

    if (toastTimeout) {
        clearTimeout(toastTimeout);
        toastTimeout = null;
    }
}

/**
 * Show loading spinner
 */
export function showSpinner(element) {
    if (!element) return;
    
    const spinner = element.querySelector('[data-spinner]');
    if (spinner) {
        spinner.classList.remove('hidden');
        spinner.classList.add('animate-spin');
    }
}

/**
 * Hide loading spinner
 */
export function hideSpinner(element) {
    if (!element) return;
    
    const spinner = element.querySelector('[data-spinner]');
    if (spinner) {
        spinner.classList.add('hidden');
        spinner.classList.remove('animate-spin');
    }
}

/**
 * Disable button with loading state
 */
export function disableButton(button, loadingText = 'Loading...') {
    if (!button) return;
    
    button.disabled = true;
    button.dataset.originalText = button.textContent;
    button.textContent = loadingText;
}

/**
 * Enable button and restore text
 */
export function enableButton(button) {
    if (!button) return;
    
    button.disabled = false;
    if (button.dataset.originalText) {
        button.textContent = button.dataset.originalText;
        delete button.dataset.originalText;
    }
}

/**
 * Format number with K/M/B suffix
 */
export function formatNumber(num) {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return num.toString();
}

/**
 * Escape HTML
 */
export function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Debounce function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
